/**
 * Canonical Task Field Groups (V4)
 * ---------------------------------
 * Single source of truth for the structure of the open TaskCard and the
 * TaskModal edit form. Both read this registry — the design-system preview
 * references a mirrored copy in `OrkaTaskCardFieldGroupsPreview.jsx` so that
 * preview and production can diverge for design iteration, then re-converge.
 *
 * Each group:
 *   id            stable key
 *   title         shown on the group head
 *   iconKey       lucide-react icon name (resolved by consumer)
 *   defaultOpen   initial disclosure state
 *   meta?         true → render as the quieter "System & Audit" group
 *   fields        [label, taskKey, kind, optionKey]
 *                 kind   ∈ text | textarea | number | date | time | select
 *                         | person | people | toggle | link | readonly
 *                 optionKey → lookup into OPTIONS, or 'wide' to span 2 cols
 */

export const TASK_FIELD_GROUPS = [
  {
    id: 'identity',
    title: 'Identity',
    iconKey: 'flag',
    defaultOpen: true,
    fields: [
      ['Title',       'task_title',      'text',     'wide'],
      ['Description', 'task_description','textarea', 'wide'],
      ['Next Steps',  'task_next_steps', 'textarea', 'wide'],
    ],
  },
  {
    id: 'classification',
    title: 'Classification',
    iconKey: 'layers',
    defaultOpen: true,
    fields: [
      ['Work Area',    'work_area_name',  'select', 'workAreas'],
      ['Related Epic', 'related_epic_name', 'select', 'epics'],
      ['Intent',       'task_intent',     'select', 'intents'],
      ['Scope',        'task_scope',      'select', 'scopes'],
    ],
  },
  {
    id: 'status-priority',
    title: 'Status & Priority',
    iconKey: 'alert',
    defaultOpen: true,
    fields: [
      ['Status',         'task_status',     'select',   'states'],
      ['Priority',       'task_priority',   'select',   'priorities'],
      ['Blocked Reason', 'task_blocked_reason', 'textarea', 'wide'],
      ['Impact',         'task_impact',     'textarea', 'wide'],
    ],
  },
  {
    id: 'schedule',
    title: 'Schedule',
    iconKey: 'calendar',
    defaultOpen: true,
    fields: [
      ['Date For Delivery', 'task_date_for_delivery', 'date'],
      ['Start Time',        'task_start_time',        'time'],
      ['End Time',          'task_end_time',          'time'],
      ['Daily Sequence',    'task_daily_sequence',    'select', 'sequence'],
    ],
  },
  {
    id: 'people',
    title: 'People',
    iconKey: 'user',
    defaultOpen: false,
    fields: [
      ['Default Responsible', 'task_assignee',    'person'],
      ['Observers',           'task_observers',   'people'],
      ['Created By',          'created_by',       'readonly'],
    ],
  },
  {
    id: 'meet',
    title: 'Google Meet & Location',
    iconKey: 'video',
    defaultOpen: false,
    fields: [
      ['Is Meeting',       'is_meeting',            'toggle'],
      ['Google Meet URL',  'google_meet_url',       'link', 'wide'],
      ['Location',         'meeting_location',      'text'],
      ['Organizer',        'meeting_organizer',     'person'],
      ['Attendees',        'meeting_attendees',     'people', 'wide'],
      ['Calendar ID',      'calendar_event_id',     'readonly', 'wide'],
    ],
  },
  {
    id: 'recurrence',
    title: 'Recurring Rules',
    iconKey: 'repeat',
    defaultOpen: false,
    fields: [
      ['Recurrence',  'task_recurrence',       'select', 'recurrence'],
      ['Edit Scope',  'task_recurrence_scope', 'select', 'recurrenceScope'],
      ['Reminder',    'task_reminders',        'select', 'reminders'],
      ['Rule',        'task_recurrence_rule',  'readonly', 'wide'],
    ],
  },
  {
    id: 'effort',
    title: 'Effort & Capacity',
    iconKey: 'clock',
    defaultOpen: false,
    fields: [
      ['Estimated Minutes',     'task_estimated_minutes',   'number'],
      ['Estimate Effort Score', 'task_estimated_effort',    'select', 'effort'],
      ['Actual Effort Score',   'task_actual_effort',       'select', 'effort'],
      ['Personal Effort',       'task_personal_effort',     'number'],
    ],
  },
  {
    id: 'planning',
    title: 'Planning & Recovery',
    iconKey: 'spark',
    defaultOpen: false,
    fields: [
      ['Reschedule Log', 'task_reschedule_log', 'textarea', 'wide'],
    ],
  },
  {
    id: 'system',
    title: 'System & Audit Metadata',
    iconKey: 'database',
    defaultOpen: false,
    meta: true,
    fields: [
      ['Task ID',                'task_id',                'readonly'],
      ['Internal ID',            'id',                     'readonly'],
      ['Imported From Calendar', 'imported_from_calendar', 'readonly'],
      ['Hidden',                 'task_hidden',            'readonly'],
      ['Created At',             'created_at',             'readonly'],
      ['Updated At',             'updated_at',             'readonly'],
      ['Source',                 'task_source',            'readonly'],
    ],
  },
];

export const TASK_FIELD_OPTIONS = {
  /* These are filled at runtime by the consumer (TaskCard, TaskModal) from
     the app's real data. We ship sensible static fallbacks for the preview. */
  sequence: ['A', 'B', 'C', 'D', 'E', 'None'],
  recurrence: ['Does not repeat', 'Daily', 'Weekly on Friday', 'Monthly', 'Custom'],
  recurrenceScope: ['This event', 'This and following', 'Entire series'],
  reminders: ['None', '10 min popup', '1 day email', '10 min popup, 1 day email'],
  effort: ['1', '2', '3', '5', '8', '13'],
  scopes: ['Work', 'Personal', 'Mixed'],
};

/** Convenience: the first 4 groups are the "primary" set shown above the
 *  Details divider on the open card. Everything else goes below. */
export const TASK_PRIMARY_GROUP_IDS = ['identity', 'classification', 'status-priority', 'schedule'];
